import { BrandMark } from "./BrandMark";
import { useEffect, useState, type ReactNode } from "react";
import {
  FolderGit2,
  Monitor,
  Settings,
  CreditCard,
  ArrowUpRight,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Sparkles,
} from "lucide-react";
export type AppView =
  "projects" | "setup" | "computers" | "billing" | "settings";
export const appViews: AppView[] = [
  "projects",
  "setup",
  "computers",
  "billing",
  "settings",
];
export const readAppView = (): AppView => {
  const value = new URLSearchParams(location.search).get("view");
  return appViews.includes(value as AppView) ? (value as AppView) : "projects";
};
const links = [
  { id: "projects", label: "Projects", icon: FolderGit2 },
  { id: "setup", label: "Set up a project", icon: Sparkles },
  { id: "computers", label: "Computers", icon: Monitor },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "settings", label: "Settings", icon: Settings },
] as const;
export function AppShell({
  view,
  setupLabel,
  navigate,
  theme,
  toggleTheme,
  signOut,
  children,
}: {
  view: AppView;
  setupLabel?: string;
  navigate: (view: AppView) => void;
  theme: "light" | "dark";
  toggleTheme: () => void;
  signOut: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const closeDrawer = () => {
    setOpen(false);
    requestAnimationFrame(() =>
      document.querySelector<HTMLButtonElement>(".mobile-menu")?.focus(),
    );
  };
  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        requestAnimationFrame(() =>
          document.querySelector<HTMLButtonElement>(".mobile-menu")?.focus(),
        );
      }
      if (e.key === "Tab") {
        const items = Array.from(
            document.querySelectorAll<HTMLElement>(
              ".workspace-sidebar a,.workspace-sidebar button",
            ),
          ),
          first = items[0],
          last = items.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    addEventListener("keydown", close);
    document
      .querySelector<HTMLAnchorElement>(
        ".workspace-sidebar nav a[aria-current=page]",
      )
      ?.focus();
    return () => removeEventListener("keydown", close);
  }, [open]);
  useEffect(() => {
    setOpen(false);
  }, [view]);
  return (
    <div className="workspace-shell">
      <a className="skip-link" href="#workspace-content">
        Skip to content
      </a>
      <aside className={"workspace-sidebar" + (open ? " is-open" : "")}>
        <a
          className="history-brand"
          href="/?view=projects"
          onClick={(e) => {
            e.preventDefault();
            navigate("projects");
            if (open) closeDrawer();
          }}
        >
          <BrandMark />
          PhaseDrift
        </a>
        <button
          className="mobile-close icon-button"
          aria-label="Close navigation"
          onClick={closeDrawer}
        >
          <X size={18} />
        </button>
        <nav aria-label="Workspace navigation">
          {links.map(({ id, label, icon: Icon }) => (
            <a
              key={id}
              href={"/?view=" + id}
              aria-current={view === id ? "page" : undefined}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                navigate(id);
                if (open) closeDrawer();
              }}
            >
              <Icon size={18} />
              <span>{id === "setup" && setupLabel ? setupLabel : label}</span>
            </a>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <a
            href="https://phasedrift.ai/docs/get-set-up"
            target="_blank"
            rel="noopener noreferrer"
          >
            Docs <ArrowUpRight size={15} />
          </a>
          <a href="mailto:hello@phasedrift.ai">
            Help & feedback <ArrowUpRight size={15} />
          </a>
          <button onClick={toggleTheme}>
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
          </button>
          <button onClick={signOut}>
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>
      {open && (
        <button
          className="sidebar-scrim"
          aria-label="Close navigation"
          onClick={closeDrawer}
        />
      )}
      <div className="workspace-body" inert={open}>
        <header className="workspace-topbar">
          <button
            className="mobile-menu icon-button"
            aria-label="Open navigation"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <Menu size={20} />
          </button>
          <span>{view === "setup" && setupLabel ? setupLabel : links.find((l) => l.id === view)?.label}</span>
          <a
            href="https://phasedrift.ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            PhaseDrift <ArrowUpRight size={14} />
          </a>
        </header>
        <div id="workspace-content" tabIndex={-1}>
          {children}
        </div>
      </div>
    </div>
  );
}
